import "./AppUserDetails.css";

import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Card } from "primereact/card";
import { Tag } from "primereact/tag";
import { Divider } from "primereact/divider";
import { ProgressSpinner } from "primereact/progressspinner";
import { Toast } from "primereact/toast";

import { getCochargeUserByEmaid, acceptTermsForUser } from "../../services/owners";
import { formatDate } from "./helpers";
import ManageOwnershipDialog from "./components/ManageOwnershipDialog";
import TermsAuditDialog from "./components/TermsAuditDialog";
import AcceptTermsDialog from "./components/AcceptTermsDialog";
import AppUserToolbar from "./components/AppUserToolbar";
import useToastActionWrapper from "../../hooks/useToastActionWrapper";
import NotificationPreferencesPanel from "./notifications/NotificationPreferencesPanel";
import StationSubscriptionsPanel from "./notifications/StationSubscriptionsPanel";
import SettingChangeHistory from "./notifications/SettingChangeHistory";

const getPermissionTagProps = (name) => {
  if (name === "admin")
    return { severity: "danger", style: { color: "#fff" } };
  if (name.startsWith("owner:"))
    return { severity: "success", style: { color: "#fff" } };
  if (name.startsWith("manager:"))
    return { severity: "info", style: { color: "#fff" } };
  return { severity: null, style: { background: "var(--surface-300)", color: "#000" } };
};

const BooleanBadge = ({ value }) => {
  if (value === null || value === undefined)
    return <span className="user-field-empty">—</span>;
  return <strong>{value ? "Yes" : "No"}</strong>;
};

const Field = ({ label, children }) => (
  <div className="user-field">
    <span className="user-field-label">{label}</span>
    <span className="user-field-value">{children ?? "—"}</span>
  </div>
);

const SectionTitle = ({ icon, label }) => (
  <p className="user-details-section-title">
    <i className={`${icon} mr-2`} />
    {label}
  </p>
);

const AppUserDetails = () => {
  const { emaid } = useParams();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ownerDialogVisible, setOwnerDialogVisible] = useState(false);
  const [auditDialogVisible, setAuditDialogVisible] = useState(false);
  const [acceptTermsDialogVisible, setAcceptTermsDialogVisible] = useState(false);
  const toastRef = useRef(null);
  const { toastActionWrapper, isLoading: acceptTermsLoading } = useToastActionWrapper(toastRef);

  const prepareUserDetails = useCallback(async (emaid) => {
    try {
      setLoading(true);
      const userData = await getCochargeUserByEmaid(emaid);
      setUser(userData);
    } catch (e) {
      console.error(
        `Failed to get user details for user with emaid: ${emaid}`,
        e,
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (emaid) {
      prepareUserDetails(emaid);
    }
  }, [emaid, prepareUserDetails]);

  if (loading)
    return (
      <div className="user-details-loading">
        <ProgressSpinner />
        <span className="text-700">Loading...</span>
      </div>
    );

  const handleTermsButtonClick = () => {
    if (user?.terms_accepted) {
      setAuditDialogVisible(true);
    } else {
      setAcceptTermsDialogVisible(true);
    }
  };

  const handleAcceptTerms = async (proofFile) => {
    const userId = user?.user_id;
    if (!userId) return;
    const success = await toastActionWrapper({
      action: () => acceptTermsForUser(userId, proofFile),
      successMessage: "Terms accepted successfully on behalf of the user.",
      errorMessage: "Failed to accept terms. Please try again.",
    });
    if (success) {
      setAcceptTermsDialogVisible(false);
      setUser((prev) => ({ ...prev, terms_accepted: true }));
      prepareUserDetails(emaid);
    }
  };

  if (!user)
    return (
      <div className="user-details-loading">
        <i className="pi pi-exclamation-circle text-4xl text-500" />
        <span className="text-700">No user data available.</span>
      </div>
    );

  return (
    <div className="user-details-page">
      <Toast ref={toastRef} />
      <ManageOwnershipDialog
        visible={ownerDialogVisible}
        onHide={() => setOwnerDialogVisible(false)}
        userEmail={user.email}
        onSuccess={() => prepareUserDetails(emaid)}
      />
      <TermsAuditDialog
        visible={auditDialogVisible}
        onHide={() => setAuditDialogVisible(false)}
        userId={user?.user_id}
      />
      <AcceptTermsDialog
        visible={acceptTermsDialogVisible}
        onHide={() => setAcceptTermsDialogVisible(false)}
        userName={user?.name || user?.email || user?.user_id}
        onAccept={handleAcceptTerms}
        loading={acceptTermsLoading}
      />

      <AppUserToolbar
        onManageOwnership={() => setOwnerDialogVisible(true)}
        onTermsAction={handleTermsButtonClick}
        termsAccepted={user?.terms_accepted}
        acceptTermsLoading={acceptTermsLoading}
      />

      <div className="user-details-header">
        <div>
          <p className="user-details-header-subtitle">App Users</p>
          <h1 className="user-details-header-title">{user.name || user.email}</h1>
          {user.name && (
            <p className="user-details-header-email">{user.email}</p>
          )}
        </div>
      </div>

      <div className="user-details-grid">
        <Card>
          <SectionTitle icon="pi pi-user" label="Profile" />
          <Field label="Name">{user.name || "—"}</Field>
          <Field label="Given Name">{user.given_name || "—"}</Field>
          <Field label="Family Name">{user.family_name || "—"}</Field>
          <Field label="Nickname">{user.nickname || "—"}</Field>
          <Field label="Emaid">{user.emaid || "—"}</Field>
          <Field label="IDP Tenant Domain">{user.idp_tenant_domain || "—"}</Field>
        </Card>

        <Card>
          <SectionTitle icon="pi pi-envelope" label="Contact" />
          <Field label="Email">{user.email || "—"}</Field>
          <Field label="Email Verified">
            <BooleanBadge value={user.email_verified} />
          </Field>
          <Field label="Phone Number">{user.phone_number || "—"}</Field>
          <Field label="Phone Verified">
            <BooleanBadge value={user.phone_verified} />
          </Field>
        </Card>

        <Card className="user-details-full-width">
          <SectionTitle icon="pi pi-key" label="Permissions" />
          {user.permissions?.length ? (() => {
            const owners = user.permissions.filter((p) => p.permission_name.startsWith("owner:"));
            const managers = user.permissions.filter((p) => p.permission_name.startsWith("manager:"));
            const others = user.permissions.filter(
              (p) => !p.permission_name.startsWith("owner:") && !p.permission_name.startsWith("manager:"),
            );
            const PermissionColumn = ({ title, items, tagSeverity, tagStyle, perItem = false }) => (
              <div className="permissions-column">
                <p className="permissions-column-title">{title}</p>
                {items.length ? (
                  items.map((p) => {
                    const props = perItem ? getPermissionTagProps(p.permission_name) : { severity: tagSeverity, style: tagStyle };
                    return (
                      <Tag
                        key={p.permission_name}
                        value={p.permission_name}
                        severity={props.severity}
                        style={props.style}
                        className="permissions-tag"
                      />
                    );
                  })
                ) : (
                  <span className="user-field-empty">None</span>
                )}
              </div>
            );
            return (
              <div className="permissions-grid">
                <PermissionColumn
                  title="Owner"
                  items={owners}
                  tagSeverity="success"
                  tagStyle={{ color: "#fff" }}
                />
                <PermissionColumn
                  title="Manager"
                  items={managers}
                  tagSeverity="info"
                  tagStyle={{ color: "#fff" }}
                />
                <PermissionColumn
                  title="Other"
                  items={others}
                  perItem
                />
              </div>
            );
          })() : (
            <span className="user-field-empty">No permissions assigned</span>
          )}
        </Card>

        {user.identities?.length > 0 && (
          <Card className="user-details-full-width">
            <SectionTitle icon="pi pi-id-card" label="Identities" />
            {user.identities.map((identity, index) => (
              <div key={index} className="user-identity-block">
                {Object.entries(identity).map(([key, val]) => (
                  <Field
                    key={key}
                    label={key
                      .replace(/_/g, " ")
                      .replace(/\b\w/g, (c) => c.toUpperCase())}
                  >
                    {val?.toString() || "—"}
                  </Field>
                ))}
                {index < user.identities.length - 1 && <Divider />}
              </div>
            ))}
          </Card>
        )}

        <Card>
          <SectionTitle icon="pi pi-shield" label="Status" />
          <Field label="Blocked">
            <BooleanBadge value={user.blocked} />
          </Field>
          <Field label="Terms Accepted">
            <BooleanBadge value={user.terms_accepted} />
          </Field>
          <Field label="Terms Accepted Date">
            {formatDate(user.terms_accepted_date)}
          </Field>
          <Field label="Multifactor">
            {user.multifactor?.join(", ") || "—"}
          </Field>
        </Card>

        <Card>
          <SectionTitle icon="pi pi-chart-line" label="Activity" />
          <Field label="Created At">{formatDate(user.created_at)}</Field>
          <Field label="Updated At">{formatDate(user.updated_at)}</Field>
          <Field label="Last Login">{formatDate(user.last_login)}</Field>
          <Field label="Last IP">{user.last_ip || "—"}</Field>
          <Field label="Logins Count">
            {user.logins_count ?? "—"}
          </Field>
        </Card>
      </div>

      <Card className="px-2 mt-4" title="Notification preferences" subTitle="What this person receives, and on which channels">
        <NotificationPreferencesPanel userId={emaid} />
      </Card>

      <Card className="px-2 mt-4" title="Station subscriptions" subTitle="Stations this person asked to hear about">
        <StationSubscriptionsPanel userId={emaid} />
      </Card>

      <Card className="px-2 mt-4" title="Notification setting history" subTitle="Who changed what, and when">
        <SettingChangeHistory userId={emaid} />
      </Card>
    </div>
  );
};

export default AppUserDetails;
